import type { Document } from "../document/Document";
import type { Layer } from "../document/Layer";
import type { Command } from "./Command";

/** A single patch to apply to one layer (visibility and/or position). */
export interface LayerCompPatch {
  readonly layerId: string;
  readonly visible?: boolean;
  readonly x?: number;
  readonly y?: number;
}

/**
 * Apply a layer comp by patching layer visibility and/or position.
 * Fully undoable — the inverse captures the original values from `before`.
 *
 * Photoshop equivalent: clicking a Layer Comp in the Layer Comps panel.
 */
export class ApplyLayerCompCommand implements Command {
  readonly kind = "apply-layer-comp";

  constructor(
    /** Human-readable label shown in the History panel. */
    private readonly compName: string,
    private readonly patches: readonly LayerCompPatch[],
  ) {}

  apply(doc: Document): Document {
    let layers: readonly Layer[] = doc.layers;
    for (const p of this.patches) {
      layers = patchLayers(layers, p);
    }
    return { ...doc, layers };
  }

  invert(before: Document, _after: Document): Command {
    // Capture the current (before) values for each patched layer.
    const inversePatches: LayerCompPatch[] = this.patches.map((p) => {
      const layer = findLayer(before.layers, p.layerId);
      const inv: { layerId: string; visible?: boolean; x?: number; y?: number } = { layerId: p.layerId };
      if ("visible" in p) inv.visible = layer?.visible ?? true;
      if ("x" in p) inv.x = (layer as any)?.x ?? 0;
      if ("y" in p) inv.y = (layer as any)?.y ?? 0;
      return inv as LayerCompPatch;
    });
    return new ApplyLayerCompCommand(`Undo ${this.compName}`, inversePatches);
  }
}

function patchLayers(layers: readonly Layer[], patch: LayerCompPatch): Layer[] {
  return layers.map((l) => {
    if (l.id === patch.layerId) {
      const updates: Record<string, unknown> = {};
      if ("visible" in patch) updates.visible = patch.visible;
      if ("x" in patch) updates.x = patch.x;
      if ("y" in patch) updates.y = patch.y;
      return { ...l, ...updates } as Layer;
    }
    if (l.kind === "group") {
      return { ...l, children: patchLayers(l.children, patch) };
    }
    return l;
  });
}

function findLayer(layers: readonly Layer[], id: string): Layer | null {
  for (const l of layers) {
    if (l.id === id) return l;
    if (l.kind === "group") {
      const inner = findLayer(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}
